import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d70id7r2m2156g4s6t20';

const openings = [
  { title: 'HVAC Technician', type: 'Full Time', market: 'Tampa, FL', dept: 'Field Operations', exp: '2+ years' },
  { title: 'HVAC Installer', type: 'Full Time', market: 'Orlando, FL', dept: 'Installation', exp: '1+ years' },
  { title: 'Plumbing Technician', type: 'Full Time', market: 'Miami, FL', dept: 'Field Operations', exp: '2+ years' },
  { title: 'Electrician', type: 'Full Time', market: 'Fort Lauderdale, FL', dept: 'Field Operations', exp: '2+ years' },
  { title: 'Home Comfort Advisor', type: 'Full Time', market: 'Fort Myers, FL', dept: 'Sales', exp: '1+ years' },
  { title: 'Install Team Lead', type: 'Full Time', market: 'Statewide Florida', dept: 'Installation', exp: '3+ years' },
  { title: 'Customer Experience Rep', type: 'Full Time / Remote', market: 'Remote', dept: 'CX', exp: 'Entry Level' },
  { title: 'Office Manager', type: 'Full Time', market: 'Jacksonville, FL', dept: 'Operations', exp: '2+ years' },
];

const CareersPage = () => {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new URLSearchParams(new FormData(e.currentTarget) as unknown as Record<string, string>);
    setStatus('submitting');
    try {
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() });
      setStatus(res.ok ? 'success' : 'error');
    } catch { setStatus('error'); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="bg-[#0C2340] py-20 nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C41E3A] text-sm font-bold uppercase tracking-widest mb-3">Careers</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5">Build Your Career at Renova</h1>
          <p className="text-xl text-white/75 max-w-2xl mx-auto">Join a fast-growing Florida home services company with competitive pay, benefits, and real advancement opportunities.</p>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-6 text-center">
            {[
              { icon: 'ri-money-dollar-circle-line', text: 'Competitive pay + bonuses' },
              { icon: 'ri-heart-pulse-line', text: 'Health, dental & vision' },
              { icon: 'ri-car-line', text: 'Company vehicle & tools' },
              { icon: 'ri-graduation-cap-line', text: 'Paid training & certs' },
              { icon: 'ri-arrow-up-line', text: 'Clear advancement paths' },
            ].map((b) => (
              <div key={b.text} className="flex flex-col items-center p-5 bg-[#F8F9FB] rounded-xl border border-[#E2E8F0]">
                <div className="w-10 h-10 bg-[#0C2340]/8 rounded-lg flex items-center justify-center mb-3">
                  <i className={`${b.icon} text-xl text-[#0C2340]`} />
                </div>
                <p className="text-xs font-semibold text-[#374151] text-center">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-8">Open Positions</h2>
              <div className="space-y-3 mb-8">
                {openings.map((o) => (
                  <div key={`${o.title}-${o.market}`} className="p-5 bg-[#F8F9FB] rounded-xl border border-[#E2E8F0] hover:border-[#C41E3A]/30 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-bold text-[#111827]">{o.title}</h3>
                        <p className="text-xs text-[#6B7280] mt-1">{o.market} · {o.type}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs bg-[#0C2340]/8 text-[#0C2340] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">{o.dept}</span>
                        <span className="text-xs text-[#9CA3AF]">{o.exp}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-[#0C2340] rounded-xl p-6 text-center">
                <p className="text-white font-bold mb-1">Don&apos;t See Your Role?</p>
                <p className="text-white/60 text-sm mb-0">Complete the form to send an open application. We&apos;re always hiring great people.</p>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">Apply Today</h2>
              {status === 'success' ? (
                <div className="text-center py-12 bg-green-50 rounded-xl border border-green-200">
                  <i className="ri-check-circle-fill text-4xl text-green-500 mb-3" />
                  <h3 className="text-xl font-bold text-[#111827]">Application Received!</h3>
                  <p className="text-[#6B7280] mt-2">Our recruiting team will be in touch within 2–3 business days.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" id="careers-form" className="space-y-4 bg-[#F8F9FB] rounded-2xl p-8 border border-[#E2E8F0]">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">First Name *</label>
                      <input name="first_name" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white" placeholder="Jane" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">Last Name *</label>
                      <input name="last_name" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white" placeholder="Smith" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Email Address *</label>
                    <input name="email" type="email" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white" placeholder="jane@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Phone Number *</label>
                    <input name="phone" type="tel" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white" placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Position Interested In</label>
                    <select name="position" className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white">
                      <option value="">Select or leave blank for open application</option>
                      {openings.map((o) => <option key={o.title}>{o.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Years of Experience</label>
                    <select name="experience" className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white">
                      <option>Entry Level (0–1 years)</option>
                      <option>1–3 years</option>
                      <option>3–5 years</option>
                      <option>5–10 years</option>
                      <option>10+ years</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">About You</label>
                    <textarea name="message" rows={4} maxLength={500} onChange={(e) => setCharCount(e.target.value.length)} className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm bg-white resize-none" placeholder="Tell us about your background and what you're looking for..." />
                    <p className="text-xs text-[#9CA3AF] mt-1">{charCount}/500</p>
                  </div>
                  {status === 'error' && <p className="text-sm text-red-600">Something went wrong. Please try again.</p>}
                  <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#C41E3A] text-white py-4 rounded-lg text-sm font-semibold hover:bg-[#A01830] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
                    {status === 'submitting' ? 'Submitting...' : 'Submit Application'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CareersPage;
